/**
 * Utility functions for storing and retrieving planning filters from local storage
 */
let USER_ID = undefined;
let USER_KEY = `user_${USER_ID}`;

// Update setUserId to also update USER_KEY
export const setUserId = (userId) => {
  USER_ID = userId;
  USER_KEY = `user_${USER_ID}`;
};

/**
 * Get a unique key for storing filters based on user and filter type
 * @param {String} filterType - Type of filter (e.g., 'header', 'sideMenu', 'employeeFilters')
 * @returns {String} Storage key
 */
export const getPlanningFilterStorageKey = (filterType) => {
  return `${USER_KEY}_planning_filters_${filterType}`;
};

/**
 * Save filters to local storage
 * @param {String} filterType - Type of filter (e.g., 'header', 'sideMenu', 'employeeFilters')
 * @param {Object} filters - Filter values to store
 */
export const saveFiltersToStorage = (filterType, filters) => {
  if (USER_ID === undefined) {
    console.error('Attempted to save planning filters with undefined userId. Aborting operation.');
    return;
  }
  try {
    const key = getPlanningFilterStorageKey(filterType);
    let stringifiedJSON = JSON.stringify(filters);
    if (stringifiedJSON != '{}') {
      localStorage.setItem(key, JSON.stringify(filters));
    }
  } catch (error) {
    console.error(`Failed to save ${filterType} filters to local storage:`, error);
  }
};

/**
 * Load filters from local storage
 * @param {String} filterType - Type of filter (e.g., 'header', 'sideMenu', 'employeeFilters')
 * @returns {Object|null} Stored filters or null if not found
 */
export const loadFiltersFromStorage = (filterType) => {
  try {
    const key = getPlanningFilterStorageKey(filterType);
    const storedFilters = localStorage.getItem(key);
    return storedFilters ? JSON.parse(storedFilters) : null;
  } catch (error) {
    console.warn(`Failed to load ${filterType} filters from local storage:`, error);
    return;
  }
};

/**
 * Clear filters from local storage
 * @param {String} filterType - Type of filter (e.g., 'header', 'sideMenu', 'employeeFilters')
 */
export const clearFiltersFromStorage = (filterType) => {
  try {
    const key = getPlanningFilterStorageKey(filterType);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to clear ${filterType} filters from local storage:`, error);
  }
};

/**
 * Clear all planning filters from local storage
 */
export const clearAllFiltersFromStorage = () => {
  try {
    // Get all keys from localStorage
    const keys = Object.keys(localStorage);

    // Filter keys that start with 'planning_filters_'
    const filterKeys = keys.filter(key => key.includes('planning_filters_'));

    // Remove each filter key
    filterKeys.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to clear all filters from local storage:', error);
  }
};

/**
 * Preserve planning filters during logout
 * Saves filters before logout and restores them after localStorage is cleared
 * @returns {Object} Object containing saved filters and planning information
*/
export const preservePlanningFiltersOnLogout = () => {
  // Get all planning filter keys
  const keys = Object.keys(localStorage);
  const filterKeys = keys.filter(key => key.includes('planning_filters_'));

  // Save the filter values
  const savedFilters = {};
  filterKeys.forEach(key => {
    savedFilters[key] = localStorage.getItem(key);
  });

  return { savedFilters };
};

/**
 * Restore planning filters after logout
 * @param {Object} savedData - Object containing saved filters and planning information
 */
export const restorePlanningFiltersAfterLogout = (savedData) => {
  const { savedFilters } = savedData;

  // Restore filters
  Object.keys(savedFilters).forEach(key => {
    localStorage.setItem(key, savedFilters[key]);
  });
};
